import { FaClock, FaEnvelope, FaMapMarkedAlt, FaPhoneAlt } from "react-icons/fa";

export const supportFAQs = {
    cctvSupport: [
        { 
            question: "Why is my CCTV camera not displaying video?", 
            answer: "Ensure that your camera is powered on and connected properly. Check the cables, network connection, and camera settings. If the issue persists, try rebooting the system or updating the firmware." 
        },
        { 
            question: "How do I access my CCTV footage remotely?", 
            answer: "To view your CCTV footage remotely, ensure your system supports remote access. Configure port forwarding on your router, use the manufacturer’s mobile app, and check that your internet connection is stable." 
        },
        { 
            question: "How can I improve the night vision of my security cameras?", 
            answer: "Make sure your camera’s infrared (IR) settings are enabled. Clean the camera lens to remove dust or obstructions, and ensure there is no strong light source interfering with the IR sensors." 
        },
        { 
            question: "What should I do if my security alarm keeps going off falsely?", 
            answer: "Check for potential false triggers like moving objects, pets, or environmental changes. Adjust the sensor sensitivity and make sure all entry points are securely closed before arming the system." 
        },
        { 
            question: "How long does my CCTV system store recorded footage?", 
            answer: "The storage duration depends on your DVR/NVR capacity and recording settings. Motion-activated recording can extend storage time. You can also back up important footage to cloud storage or an external hard drive." 
        },
        { 
            question: "What is the best way to maintain my CCTV cameras?", 
            answer: "Regularly clean the lenses, check for loose cables, update firmware, and inspect power supplies to ensure optimal performance. Periodic professional maintenance is also recommended." 
        }
    ],
    installationGuides: [
        { 
            question: "How do I install my CCTV cameras properly?", 
            answer: "Choose optimal locations with a clear field of view and minimal obstructions. Mount the cameras securely, connect the cables, and ensure proper power supply. Adjust angles for maximum coverage." 
        },
        { 
            question: "What are the best locations to install security cameras?", 
            answer: "Install cameras at entry points, driveways, hallways, and areas with high traffic. Ensure they cover blind spots while avoiding direct exposure to sunlight or reflections." 
        },
        { 
            question: "Do I need professional installation, or can I do it myself?", 
            answer: "Many CCTV systems offer DIY installation with easy plug-and-play setups. However, for advanced systems with multiple cameras and hardwired connections, professional installation is recommended." 
        },
        { 
            question: "How do I connect my CCTV system to my smartphone?", 
            answer: "Download the manufacturer’s mobile app, connect your DVR/NVR to the internet, and configure remote access settings. Ensure port forwarding is enabled for external viewing." 
        },
        { 
            question: "What type of cable should I use for CCTV installation?", 
            answer: "Use **coaxial cables (RG59)** for analog cameras and **Ethernet cables (Cat5e/Cat6)** for IP cameras. Ensure proper insulation and protection against environmental damage." 
        },
        { 
            question: "How can I ensure my CCTV system is weatherproof?", 
            answer: "Use **IP66 or IP67-rated** cameras for outdoor installation. Seal cable connections properly and use weatherproof junction boxes to protect power and data cables from moisture." 
        }
    ],
    securityUpdates: [
        { 
            question: "How often should I update my CCTV system’s firmware?", 
            answer: "It's recommended to check for firmware updates every **3-6 months** to ensure your system has the latest security patches and features." 
        },
        { 
            question: "Why is updating my CCTV firmware important?", 
            answer: "Firmware updates fix security vulnerabilities, improve system performance, and add new features. Regular updates help protect against cyber threats and unauthorized access." 
        },
        { 
            question: "How do I update my CCTV system's firmware?", 
            answer: "Visit the manufacturer's website, download the latest firmware, and follow the update instructions via your DVR/NVR or camera settings menu." 
        },
        { 
            question: "What should I do if my CCTV system stops working after an update?", 
            answer: "Try restarting the system. If issues persist, revert to a previous version (if possible) or contact technical support for troubleshooting." 
        },
        { 
            question: "Can hackers exploit outdated CCTV systems?", 
            answer: "Yes, outdated systems are vulnerable to hacking. Regular updates and strong passwords help prevent unauthorized access and cyber threats." 
        },
        { 
            question: "How can I ensure my CCTV system stays secure?", 
            answer: "Keep firmware updated, change default passwords, enable encryption, restrict remote access, and use firewalls to protect your network from unauthorized intrusions." 
        }
    ],
    cameraIssues: [
        { 
            question: "Why is my CCTV camera not displaying any video?", 
            answer: "Check the power supply, cables, and connections. Ensure the camera is properly powered and the DVR/NVR is functioning correctly. Try rebooting the system." 
        },
        { 
            question: "What should I do if my CCTV camera shows a black screen?", 
            answer: "This may be due to a faulty power adapter, a damaged video cable, or an incorrect input selection on your DVR/NVR. Check these components and replace if necessary." 
        },
        { 
            question: "Why is my CCTV camera showing a blurry or distorted image?", 
            answer: "Clean the camera lens, adjust the focus settings, and ensure there is no condensation inside the camera housing. Also, check for interference from nearby electrical sources." 
        },
        { 
            question: "My CCTV camera is not recording. What should I check?", 
            answer: "Ensure that recording is enabled in the settings, check if the hard drive has enough space, and verify that the recording schedule is set correctly." 
        },
        { 
            question: "Why is my CCTV camera showing ‘No Signal’?", 
            answer: "Check if the camera is properly connected to the DVR/NVR, verify power supply, and inspect the cables for any damage or loose connections." 
        },
        { 
            question: "How do I fix night vision issues on my CCTV camera?", 
            answer: "Ensure that the infrared (IR) LEDs are working, remove any reflective objects near the camera, and check for obstructions that may block IR light." 
        },
        { 
            question: "My CCTV camera keeps disconnecting. What could be the issue?", 
            answer: "If it's a wired camera, check for loose or damaged cables. For wireless cameras, ensure a strong Wi-Fi signal, update firmware, and reduce interference from other devices." 
        }
    ],
    remoteAccess: [
        { 
            question: "How do I access my CCTV system remotely?", 
            answer: "To access your CCTV remotely, ensure your DVR/NVR supports remote viewing, connect it to the internet, and use the official mobile app or web client provided by the manufacturer." 
        },
        { 
            question: "Why can't I connect to my CCTV system remotely?", 
            answer: "Check if your internet connection is stable, ensure port forwarding is correctly set up, and verify that the mobile app or software is configured with the right credentials." 
        },
        { 
            question: "What ports need to be open for remote access?", 
            answer: "Most DVRs/NVRs require ports like 80, 554, or custom manufacturer-specified ports to be open. Check your device manual and configure port forwarding on your router." 
        },
        { 
            question: "Do I need a static IP for remote CCTV access?", 
            answer: "A static IP makes remote access easier, but you can also use DDNS (Dynamic DNS) services if you have a dynamic IP address from your ISP." 
        },
        { 
            question: "Which app should I use to view my CCTV remotely?", 
            answer: "Use the official mobile app recommended by your CCTV manufacturer. Common apps include Hik-Connect, iVMS-4500, XMEye, and SwannView Link." 
        },
        { 
            question: "How can I improve remote access performance?", 
            answer: "Ensure you have a high-speed internet connection, limit the number of devices streaming simultaneously, and adjust video quality settings in the app." 
        },
        { 
            question: "Is remote access secure?", 
            answer: "Yes, but ensure you use strong passwords, enable two-factor authentication if available, and update your firmware to protect against vulnerabilities." 
        }
    ],
    dataStorage: [
        { 
            question: "How long can my CCTV system store recorded footage?", 
            answer: "The storage duration depends on the hard drive capacity, recording quality, frame rate, and whether motion detection is enabled. Most systems can store footage for 7 to 30 days." 
        },
        { 
            question: "Can I expand my CCTV storage capacity?", 
            answer: "Yes, you can expand storage by upgrading to a larger hard drive, using an external storage device, or integrating cloud storage services if supported by your system." 
        },
        { 
            question: "What type of hard drive is best for CCTV storage?", 
            answer: "Surveillance-grade hard drives (such as WD Purple or Seagate SkyHawk) are designed for 24/7 recording and offer better durability than regular desktop hard drives." 
        },
        { 
            question: "Does my CCTV system support cloud storage?", 
            answer: "Some modern CCTV systems support cloud storage options. Check with your manufacturer for cloud compatibility and subscription details." 
        },
        { 
            question: "How can I back up my CCTV footage?", 
            answer: "You can back up footage using an external hard drive, USB storage, or cloud services. Many DVR/NVR systems allow scheduled backups to prevent data loss." 
        },
        { 
            question: "Why is my CCTV hard drive full so quickly?", 
            answer: "High-resolution recording, 24/7 continuous recording, and multiple cameras can fill up storage fast. Enabling motion detection and adjusting video quality can help optimize storage usage." 
        },
        { 
            question: "Can deleted CCTV footage be recovered?", 
            answer: "Once footage is overwritten, it is difficult to recover. Some data recovery tools might help, but it's best to set up automatic backups to avoid loss." 
        }
    ],
    motionSensors: [
        { 
            question: "How do motion sensors in CCTV systems work?", 
            answer: "Motion sensors detect movement using infrared or microwave technology. When motion is detected, the CCTV system can trigger recording, send alerts, or activate alarms." 
        },
        { 
            question: "Why is my motion sensor triggering false alarms?", 
            answer: "False alarms can be caused by pets, changing lighting conditions, or moving objects like curtains. Adjusting the sensor sensitivity and setting motion detection zones can help reduce false triggers." 
        },
        { 
            question: "Can I adjust the sensitivity of my motion sensors?", 
            answer: "Yes, most CCTV systems allow you to adjust motion detection sensitivity in the settings. Lowering sensitivity can reduce false alerts, while increasing it ensures small movements are captured." 
        },
        { 
            question: "Do motion sensors work in the dark?", 
            answer: "Yes, most motion sensors use infrared (PIR) technology, which detects body heat and movement even in complete darkness." 
        },
        { 
            question: "How can I improve the accuracy of my motion sensors?", 
            answer: "Ensure sensors are placed at the right height, avoid direct exposure to sunlight, and define motion detection zones to focus on critical areas." 
        },
        { 
            question: "Can motion sensors differentiate between humans and animals?", 
            answer: "Some advanced CCTV systems use AI-powered motion detection to differentiate between humans, animals, and vehicles, reducing false alerts." 
        },
        { 
            question: "Will motion sensors work through glass or windows?", 
            answer: "Standard PIR motion sensors do not work through glass, as they detect infrared radiation, which glass blocks. Microwave motion sensors, however, can detect movement through glass." 
        }
    ],
    technicalSupport: [
        { 
            question: "How can I contact technical support?", 
            answer: "You can reach our technical support team via phone, email, or live chat. Visit our 'Contact Us' page for details." 
        },
        { 
            question: "What should I do if my CCTV system stops working?", 
            answer: "First, check power connections, internet connectivity, and camera cables. If the issue persists, restart the system and consult the user manual. Contact our support team for further assistance." 
        },
        { 
            question: "Can I reset my CCTV system to factory settings?", 
            answer: "Yes, most CCTV systems have a reset button or an option in the settings menu to restore factory defaults. Refer to your system's manual for specific instructions." 
        },
        { 
            question: "How can I update my CCTV system firmware?", 
            answer: "Firmware updates can be downloaded from the manufacturer’s website. Follow the update instructions carefully to avoid system issues." 
        },
        { 
            question: "Why is my CCTV system showing a black screen?", 
            answer: "A black screen may indicate power loss, disconnected cables, or incorrect input settings on your monitor. Ensure all connections are secure and restart the system." 
        },
        { 
            question: "How do I troubleshoot camera connection issues?", 
            answer: "Check the network settings, power supply, and cable connections. If using Wi-Fi cameras, ensure they are within range of the router and the signal is strong." 
        },
        { 
            question: "Does my CCTV system come with a warranty?", 
            answer: "Yes, our CCTV systems come with a standard warranty. The duration and coverage details vary by model. Contact support for warranty claims." 
        },
        { 
            question: "How do I schedule a maintenance check for my security system?", 
            answer: "You can schedule a maintenance check by contacting our support team. Regular maintenance ensures optimal performance and prevents potential failures." 
        }
    ],

};

export const supportContacts = [
    {
        id: 1,
        icon: <FaMapMarkedAlt />,
        title: "address",
        content: ["Secura Support Center, 789 Security Lane"],
    },
    {
        id: 2,
        icon: <FaPhoneAlt />,
        title: "call us",
        content: ["+123-456-7890", "+111-222-333"],
    },
    {
        id: 3,
        icon: <FaEnvelope />,
        title: "mail us",
        content: ["support@example.com", "help@example.com"],
    },
    {
        id: 4,
        icon: <FaClock />,
        title: "opening hours",
        content: ["Mon - Fri: 7 AM - 7 PM", "Sat - Sun: 8 AM - 6 PM"],
    },
];